import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { getLmsErrorMessage, getLmsListItems } from "../../lms/api";
import { formatDateTime } from "../../lms/components/LmsPrimitives";
import { statusColor } from "../../lms/constants";

const { Text } = Typography;

export default function LearnerLiveSessions({ api, studentID }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(null);
  const [passcodes, setPasscodes] = useState({});
  const [now, setNow] = useState(Date.now());
  const mounted = useRef(false);
  const popups = useRef(new Set());

  useEffect(() => {
    mounted.current = true;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    const polling = setInterval(() => setRefresh((value) => value + 1), 30000);
    const pendingPopups = popups.current;
    return () => {
      mounted.current = false;
      clearInterval(timer);
      clearInterval(polling);
      pendingPopups.forEach((popup) => popup.close());
    };
  }, []);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      try {
        const response = await api.learnerLiveSessions({
          studentID,
          page,
          pageSize: 10,
        });
        if (!active) return;
        if (!response?.ok) {
          setRows([]);
          setTotal(0);
          setError(
            getLmsErrorMessage(response, "Unable to load live sessions"),
          );
        } else {
          const result = getLmsListItems(response.data);
          setRows(result.items);
          setTotal(result.count);
          setError("");
        }
      } catch (err) {
        if (active) {
          setRows([]);
          setTotal(0);
          setError("Unable to load live sessions. Please try again.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [api, studentID, page, refresh]);

  const openSession = async (record, replay = false) => {
    const popup = window.open("", "_blank");
    if (!popup) {
      message.warning("Please enable popups for this site, then try again.");
      return;
    }
    popup.opener = null;
    popup.document.title = replay ? "Opening recording…" : "Opening Zoom…";
    popup.document.body.textContent = "Preparing your secure link…";
    popups.current.add(popup);
    setBusy(record.id);
    try {
      const response = replay
        ? await api.liveSessions.recording(record.id, studentID)
        : await api.liveSessions.joinUrl(record.id, "participant", studentID);
      if (!mounted.current) {
        popup.close();
        return;
      }
      if (!response?.ok) {
        throw new Error(
          getLmsErrorMessage(response, "Unable to open this session"),
        );
      }
      const url = replay ? response.data?.recordingUrl : response.data?.joinUrl;
      if (!url) {
        popup.close();
        message.info(
          replay
            ? "The recording is not ready yet"
            : "The join link is not available yet",
        );
        return;
      }
      const destination = new URL(url);
      if (destination.protocol !== "https:")
        throw new Error("The session returned an invalid link");
      if (!replay)
        setPasscodes((current) => ({
          ...current,
          [record.id]: response.data?.passcode || null,
        }));
      if (!popup.closed) popup.location.replace(destination.href);
    } catch (err) {
      popup.close();
      if (mounted.current)
        message.error(err.message || "Unable to open this session");
    } finally {
      popups.current.delete(popup);
      if (mounted.current) {
        setBusy(null);
        setRefresh((value) => value + 1);
      }
    }
  };

  return (
    <Card
      title="Live classes and recordings"
      style={{ marginBottom: 16 }}
      extra={
        <Button
          type="text"
          icon={<ReloadOutlined />}
          aria-label="Refresh live sessions"
          onClick={() => setRefresh((value) => value + 1)}
        />
      }
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        rowKey="id"
        dataSource={rows}
        loading={loading}
        scroll={{ x: 850 }}
        pagination={{
          current: page,
          pageSize: 10,
          total,
          showSizeChanger: false,
          onChange: setPage,
        }}
        locale={{
          emptyText: "No live classes or recordings are available yet",
        }}
        columns={[
          {
            title: "Class",
            dataIndex: "title",
            render: (title, row) => (
              <Space direction="vertical" size={0}>
                <Text strong>{title}</Text>
                <Text type="secondary">
                  {[
                    row.sessionTitle,
                    row.subjectName,
                    row.className,
                    row.streamName,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </Space>
            ),
          },
          { title: "Teacher", dataIndex: "teacherName" },
          {
            title: "Scheduled",
            dataIndex: "scheduledAt",
            render: (value) => formatDateTime(value),
          },
          {
            title: "Duration",
            dataIndex: "durationMinutes",
            render: (value) => `${value} min`,
          },
          {
            title: "Status",
            dataIndex: "status",
            render: (status, row) => (
              <Tag color={statusColor(status)}>
                {row.statusDisplay || status}
              </Tag>
            ),
          },
          {
            title: "Join / replay",
            render: (_, row) => {
              const untilJoin = new Date(row.joinableAt).getTime() - now;
              return (
                <Space direction="vertical" size={4}>
                  {["Scheduled", "Live"].includes(row.status) && (
                    <>
                      <Button
                        type="primary"
                        disabled={!row.canJoin || busy !== null}
                        loading={busy === row.id}
                        onClick={() => openSession(row)}
                      >
                        Join
                      </Button>
                      {!row.canJoin && (
                        <Text type="secondary">
                          {untilJoin > 0
                            ? `Join opens in ${Math.ceil(untilJoin / 60000)} min (${formatDateTime(row.joinableAt)})`
                            : "Joining is currently unavailable"}
                        </Text>
                      )}
                    </>
                  )}
                  {row.status === "Completed" &&
                    (row.recordingUrl ? (
                      <Button
                        disabled={busy !== null}
                        loading={busy === row.id}
                        onClick={() => openSession(row, true)}
                      >
                        Watch replay
                      </Button>
                    ) : (
                      <Text type="secondary">No recording available</Text>
                    ))}
                  {passcodes[row.id] && (
                    <Text copyable={{ text: passcodes[row.id] }}>
                      Passcode: {passcodes[row.id]}
                    </Text>
                  )}
                </Space>
              );
            },
          },
        ]}
      />
    </Card>
  );
}
